import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  LinearProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useAuth } from '../context/AuthContext';
import ChartModal from './ChartModal';
import { getPositionSideLabel } from '../utils/positions';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface EODViewProps {
  instanceName: string;
}

interface AccountData {
  account_id: string;
  account_number: string;
  equity: number;
  last_equity?: number;
  cash: number;
  buying_power: number;
  daytrading_buying_power: number;
  portfolio_value: number;
  pattern_day_trader: boolean;
  multiplier: string;
  status: string;
}

interface TradePair {
  symbol: string;
  side: string;
  qty: number;
  entry_price: number;
  entry_time_et: string;
  exit_price: number;
  exit_time_et: string;
  realized_pnl: number;
  entry_order_ids: string[];
  exit_order_ids: string[];
  holding_minutes: number;
  instrument_type?: string;
}

interface SymbolTotals {
  [symbol: string]: {
    trades: number;
    realized_pnl: number;
  };
}

interface InstrumentTypeBreakdown {
  EQUITY: {
    trades: number;
    realized_pnl: number;
    unique_symbols: number;
  };
  OPTION: {
    trades: number;
    realized_pnl: number;
    unique_symbols: number;
  };
}

// New CSP / premium-selling EOD format (e.g. GROK): positions are held
// overnight, so realized "pairs" are empty and the meaningful data is the
// day's fills (today_trades) and the open positions carried forward.
interface TodayTrade {
  symbol: string;
  asset_type: string;
  action: string; // SELL / BUY
  qty: number;
  fill_price: number;
  multiplier: number;
  premium: number;
  cash_flow: number;
  time_et: string;
  order_id: string;
  underlying: string;
  option_type: string; // PUT / CALL
  strike: number;
  expiry: string;
}

interface OpenPosition {
  symbol: string;
  asset_type: string;
  side: string; // short / long
  qty: number;
  avg_entry_price: number;
  current_price: number;
  market_value: number;
  cost_basis: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  unrealized_intraday_pnl: number;
  underlying: string;
  option_type: string;
  strike: number;
  expiry: string;
  dte: number;
  premium_collected: number;
  // CSP wheel v2 extras (optional)
  collateral_locked?: number;
  return_on_premium_pct?: number;
  opened_at?: string;
  days_held?: number;
}

interface OpenPositionsSummary {
  num_positions: number;
  total_premium_collected: number;
  total_unrealized_pnl: number;
  total_unrealized_intraday_pnl: number;
}

// ---- CSP weekly-wheel format (e.g. GROK) ----
interface EntryToday {
  time_et: string;
  action: string;
  underlying: string;
  option_type: string;
  strike: number;
  expiry: string;
  premium_collected: number;
  realized_pnl: number | null;
}

interface ExitToday {
  time_et: string;
  event: string; // BUY_TO_CLOSE_PUT, EXPIRED_OTM, ASSIGNED, CALLED_AWAY, ...
  action: string;
  underlying: string;
  option_type: string;
  strike: number;
  expiry: string;
  occ?: string;
  qty?: number | null;
  fill_price?: number | null;
  premium_collected?: number | null;
  realized_pnl: number;
}

interface PeriodScope {
  premium_collected: number;
  realized_pnl: number;
  num_entries: number;
  num_exits: number;
  wins: number;
  losses: number;
  win_rate: number | null; // wins/(wins+losses); null if nothing closed
  num_assignments: number;
}

interface PeriodBlock {
  week_start: string;
  week_to_date: PeriodScope;
  cumulative: PeriodScope;
}

// ---- CSP wheel v2 headline scoreboard / decomposition / capital ----
interface PerformanceScope {
  realized_pnl?: number;
  premium_collected?: number;
  net_cashflow?: number;
  unrealized_change?: number;
  day_pnl_mtm?: number;
  num_entries?: number;
  num_exits?: number;
  wins?: number;
  losses?: number;
  win_rate?: number | null;
  num_assignments?: number;
  unrealized_pnl?: number;
  total_pnl?: number;
}

interface PerformanceBlock {
  today?: PerformanceScope;
  week_to_date?: PerformanceScope;
  cumulative?: PerformanceScope;
}

interface PnlSummary {
  realized_cumulative: number;
  premium_collected_cumulative: number;
  unrealized_total: number;
  unrealized_shares: number;
  unrealized_options: number;
  total_pnl: number;
  total_return_pct: number;
}

interface CapitalBlock {
  put_collateral: number;
  shares_cost_basis: number;
  shares_market_value: number;
  capital_deployed: number;
  pct_of_equity: number;
  cash_available: number;
  shares_held_total: number;
  names_holding_shares: number;
}

interface WeeklySeriesPoint {
  week_start: string;
  premium_collected: number;
  realized_pnl: number;
  num_entries: number;
  num_exits: number;
  wins: number;
  losses: number;
  win_rate: number | null;
  num_assignments: number;
  cumulative_realized: number;
}

interface DailySeriesPoint {
  date: string;
  daily_realized: number;
  cumulative_realized: number;
  // v2 enrichment (optional)
  premium_collected?: number;
  num_entries?: number;
  num_exits?: number;
  num_assignments?: number;
}

interface PerSymbolPeriodEntry {
  today_realized: number;
  period_realized: number;
  period_premium_collected: number;
  period_entries: number;
  period_exits: number;
}

interface WheelStateEntry {
  state: string; // NO_POSITION | SHORT_PUT | LONG_SHARES | SHORT_CALL
  option_symbol?: string | null;
  strike?: number | null;
  expiry?: string | null;
  dte?: number | null;
  contracts?: number;
  entry_credit_total?: number;
  shares_held?: number;
  cost_basis_per_share?: number | null;
  cash_locked?: number;
}

interface AssignmentRisk {
  symbol: string;
  option_symbol: string;
  strike: number;
  spot: number;
  distance_pct: number; // negative = ITM by this %
  dte: number;
  expiry: string;
  contracts: number;
  cost_basis_if_assigned: number;
  cash_required_if_assigned: number;
}

interface AssignedShare {
  symbol: string;
  shares: number;
  cost_basis_per_share: number;
  current_price: number;
  market_value: number;
  unrealized_pnl_shares: number;
  covered_call_active: boolean;
  call_strike?: number | null;
  call_expiry?: string | null;
  call_dte?: number | null;
}

// ---- Spread-centric PCS (Put Credit Spread) format (e.g. OPTIONS PCS) ----
interface SpreadLeg {
  role?: string; // short | long
  symbol?: string;
  option_type?: string;
  strike?: number;
  expiry?: string;
  current_price?: number;
  unrealized_pl?: number;
  unrealized_intraday_pl?: number;
  marked?: boolean;
}

interface OpenSpread {
  underlying: string;
  structure: string;
  state?: string;
  short_leg_symbol?: string;
  long_leg_symbol?: string;
  short_strike: number;
  long_strike: number;
  width: number;
  expiry: string;
  dte: number;
  contracts: number;
  opened_at: string;
  days_held: number;
  entry_credit_per_share: number;
  entry_credit_total: number;
  max_loss_total: number;
  cash_locked: number;
  current_spread_mid_per_share: number;
  current_cost_to_close_total: number;
  unrealized_pnl: number;
  unrealized_intraday_pnl: number;
  pct_of_credit_captured: number;
  pct_of_max_loss_used: number;
  profit_target_per_share?: number;
  max_loss_trigger_per_share?: number;
  best_mid_seen_per_share?: number;
  legs?: SpreadLeg[];
}

interface ClosedSpreadTrade {
  underlying: string;
  structure?: string;
  short_strike: number;
  long_strike: number;
  width: number;
  contracts?: number;
  opened_at: string;
  closed_at: string;
  hold_days: number;
  exit_kind: string;
  exit_reason: string;
  entry_credit_per_share?: number;
  entry_credit_total: number;
  close_debit_total: number;
  realized_pnl: number;
  pct_of_credit_captured: number;
}

interface PcsConfig {
  profit_close_pct?: number;
  max_loss_close_pct?: number;
  dte_mgmt_threshold?: number;
  max_open_spreads?: number;
}

interface PcsSummary {
  num_open_spreads: number;
  open_credit_at_risk: number;
  open_max_loss_total: number;
  open_cash_locked: number;
  open_unrealized_pnl: number;
  open_unrealized_intraday_pnl: number;
  entries_today_count: number;
  exits_today_count: number;
  premium_collected_today: number;
  realized_pnl_today: number;
  day_pnl_estimate: number;
  premium_collected_all_time: number;
  realized_pnl_all_time: number;
  num_closed_all_time: number;
  num_wins: number;
  num_losses: number;
  win_rate: number | null;
}

// ---- CSP Wheel SCHEMA v2 format (e.g. E-OPTIONS) ----
interface OpenBookSummary {
  num_positions: number;
  total_premium_collected: number;
  total_collateral: number;
  collateral_utilization_pct: number;
  total_unrealized_pnl: number;
  total_unrealized_pnl_pct: number;
  total_unrealized_intraday_pnl?: number;
}

interface LifetimeTradeRef {
  underlying: string;
  option_type: string;
  strike: number;
  realized_pnl: number;
  close_reason: string;
  closed_at: string;
}

interface LifetimeStats {
  first_close_date?: string;
  last_close_date?: string;
  num_closed_trades: number;
  wins: number;
  losses: number;
  scratches?: number;
  win_rate_pct: number;
  gross_premium_collected: number;
  realized_pnl: number;
  avg_realized_per_trade: number;
  avg_days_held: number;
  by_reason: { [reason: string]: { count: number; realized_pnl: number } };
  best_trade?: LifetimeTradeRef;
  worst_trade?: LifetimeTradeRef;
}

interface WheelClosedTrade {
  underlying: string;
  option_type: string;
  strike: number;
  expiry: string;
  contracts: number;
  entry_occ?: string;
  entry_price: number;
  entry_premium: number;
  opened_at: string;
  exit_price?: number;
  exit_cost?: number;
  closed_at: string;
  realized_pnl: number;
  return_on_premium_pct: number;
  days_held: number;
  close_reason: string;
  reason_category: string;
}

interface WheelEntryToday {
  underlying: string;
  option_type: string;
  strike: number;
  expiry: string;
  contracts: number;
  entry_occ?: string;
  entry_price: number;
  premium_collected: number;
  collateral_locked?: number;
  dte_at_entry?: number;
  opened_at: string;
}

interface EODData {
  date_et: string;
  generated_at_et: string;
  account: AccountData;
  realized_pnl_day_from_pairs: number;
  num_pairs: number;
  per_symbol_totals: SymbolTotals;
  per_instrument_type?: InstrumentTypeBreakdown;
  pairs: TradePair[];
  // New CSP / premium-selling format (optional)
  today_trades?: TodayTrade[];
  open_positions?: OpenPosition[];
  open_positions_summary?: OpenPositionsSummary;
  day_pnl_estimate?: number;
  premium_collected_today_gross?: number;
  premium_paid_today_gross?: number;
  net_cashflow_today?: number;
  // CSP weekly-wheel format (optional)
  realized_pnl_today?: number;
  entries_today?: EntryToday[];
  exits_today?: ExitToday[];
  period?: PeriodBlock;
  // CSP weekly-wheel v2 enrichment (optional)
  performance?: PerformanceBlock;
  pnl_summary?: PnlSummary;
  capital?: CapitalBlock;
  weekly_series?: WeeklySeriesPoint[];
  daily_series?: DailySeriesPoint[];
  winning_days?: number;
  losing_days?: number;
  per_symbol_period?: { [symbol: string]: PerSymbolPeriodEntry };
  period_source?: string;
  wheel_state?: { [symbol: string]: WheelStateEntry };
  wheel_state_counts?: { [state: string]: number };
  cash_locked_total?: number;
  num_names_tracked?: number;
  assignment_risk?: AssignmentRisk[];
  assigned_shares?: AssignedShare[];
  // Spread-centric PCS format (optional)
  strategy?: string;
  pcs_account_id?: string;
  config?: PcsConfig;
  open_spreads?: OpenSpread[];
  closed_trades?: ClosedSpreadTrade[];
  summary?: PcsSummary;
  // CSP Wheel v2 format (optional)
  schema_version?: number;
  day_pnl_true?: number;
  premium_collected_today?: number;
  premium_paid_today?: number;
  open_book_summary?: OpenBookSummary;
  lifetime?: LifetimeStats;
}

interface DailyData {
  date: string;
  eodData: EODData;
  actual_daily_pnl: number | null;
  actual_daily_pnl_percent: number | null;
}

interface PeriodMetrics {
  totalPnL: number;
  totalRealizedPnL: number;
  avgDailyPnL: number;
  bestDay: { date: string; pnl: number } | null;
  worstDay: { date: string; pnl: number } | null;
  winningDays: number;
  losingDays: number;
  winRate: number;
  symbolPerformance: { [symbol: string]: { trades: number; pnl: number } };
  totalPnLPercent: number;
  startingEquity: number;
}

// Run async tasks with limited concurrency. Each day's EOD fetch opens its
// own SSH/SFTP connection on the backend, so firing an entire month at once
// can trip the server's simultaneous-connection limits and cause transient
// "missing day" failures. Capping concurrency keeps the range reliable while
// still loading in parallel.
async function settleWithConcurrency<T>(
  items: string[],
  worker: (item: string) => Promise<T>,
  limit: number
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = new Array(items.length);
  let next = 0;
  const runnerCount = Math.max(1, Math.min(limit, items.length));
  const runners = new Array(runnerCount).fill(0).map(async () => {
    while (next < items.length) {
      const current = next++;
      try {
        results[current] = { status: 'fulfilled', value: await worker(items[current]) };
      } catch (e) {
        results[current] = { status: 'rejected', reason: e };
      }
    }
  });
  await Promise.all(runners);
  return results;
}

// Wheel lifecycle state -> color (NO_POSITION idle, SHORT_PUT collecting,
// LONG_SHARES assigned, SHORT_CALL selling calls).
const WHEEL_STATE_COLORS: { [state: string]: string } = {
  NO_POSITION: '#9e9e9e',
  SHORT_PUT: '#1976d2',
  LONG_SHARES: '#f9a825',
  SHORT_CALL: '#9c27b0',
};
const wheelStateColor = (state: string): string => WHEEL_STATE_COLORS[state] || '#9e9e9e';

const EODView: React.FC<EODViewProps> = ({ instanceName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { token } = useAuth();

  // Display name mapping for instances
  const INSTANCE_DISPLAY_NAMES: { [key: string]: string } = {
    'V1': 'OPTIONS',
    'E_OPTIONS': 'E-OPTIONS',
    'OPTIONS_PCS': 'OPTIONS PCS',
    'TOPORB': 'TIER2',
    'LIVE': 'LIVE',
    'FUTURES': 'Futures',
  };

  const displayName = INSTANCE_DISPLAY_NAMES[instanceName.toUpperCase()] || instanceName;

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [dailyDataMap, setDailyDataMap] = useState<Map<string, DailyData>>(new Map());
  const [periodMetrics, setPeriodMetrics] = useState<PeriodMetrics | null>(null);
  const [missingDates, setMissingDates] = useState<string[]>([]);
  
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');

  // Chart modal handlers
  const handleSymbolClick = (symbol: string) => {
    setSelectedSymbol(symbol);
    setChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setChartModalOpen(false);
    setSelectedSymbol('');
  };

  // Fetch available dates on mount
  useEffect(() => {
    fetchAvailableDates();
  }, [instanceName]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await fetch(`/api/instances/${instanceName}/eod-dates`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch EOD dates');
      
      const data = await response.json();
      const dates = data.dates || [];
      setAvailableDates(dates);
      
      // Auto-select latest date as both start and end (single day view by default)
      if (dates.length > 0) {
        setStartDate(dates[0]);
        setEndDate(dates[0]);
      }
    } catch (err) {
      console.error('Error fetching EOD dates:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [instanceName]);

  // Fetch EOD data for date range
  useEffect(() => {
    if (startDate && endDate) {
      fetchDateRangeData();
    }
  }, [startDate, endDate]);

  // Fetch a single day's EOD data. Throws on a non-OK response so callers
  // can decide how to handle individual-day failures.
  const fetchSingleDay = async (date: string): Promise<DailyData> => {
    const response = await fetch(`/api/instances/${instanceName}/eod-data?date=${date}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error(`Failed to fetch EOD data for ${date}`);
    const result = await response.json();
    return {
      date,
      eodData: result.data,
      actual_daily_pnl: result.actual_daily_pnl,
      actual_daily_pnl_percent: result.actual_daily_pnl_percent,
    };
  };

  const fetchDateRangeData = async () => {
    setLoading(true);
    setError(null);
    setMissingDates([]);

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const datesToFetch: string[] = [];

      // Generate list of dates to fetch
      for (const date of availableDates) {
        const d = new Date(date);
        if (d >= start && d <= end) {
          datesToFetch.push(date);
        }
      }

      // Fetch dates with limited concurrency, tolerating individual failures
      // so a single missing/unreadable day doesn't wipe out the whole range.
      const settled = await settleWithConcurrency(datesToFetch, fetchSingleDay, 6);

      const results: DailyData[] = [];
      const failedDates: string[] = [];
      settled.forEach((outcome, idx) => {
        if (outcome.status === 'fulfilled') {
          results.push(outcome.value);
        } else {
          failedDates.push(datesToFetch[idx]);
        }
      });

      // Build map of daily data from the days that loaded successfully
      const newMap = new Map<string, DailyData>();
      results.forEach((r) => {
        newMap.set(r.date, r);
      });
      setDailyDataMap(newMap);
      setMissingDates(failedDates);

      // Calculate period metrics from the days we were able to load
      calculatePeriodMetrics(results);

      // Auto-expand if only a single day loaded
      if (results.length === 1) {
        setExpandedDays(new Set([results[0].date]));
      }

      // Only treat as a hard error if we couldn't load ANY day in the range
      if (results.length === 0 && datesToFetch.length > 0) {
        setError(`Failed to fetch EOD data for the selected range (${datesToFetch.length} day${datesToFetch.length !== 1 ? 's' : ''}).`);
      }
    } catch (err) {
      console.error('Error fetching date range data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setDailyDataMap(new Map());
      setPeriodMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  // Retry only the days that previously failed, merging successes into the
  // existing data without disturbing days that already loaded.
  const retryMissingDates = async () => {
    if (missingDates.length === 0) return;
    setLoading(true);

    const datesToRetry = [...missingDates];
    const settled = await settleWithConcurrency(datesToRetry, fetchSingleDay, 6);

    const newMap = new Map(dailyDataMap);
    const stillFailed: string[] = [];
    settled.forEach((outcome, idx) => {
      if (outcome.status === 'fulfilled') {
        newMap.set(outcome.value.date, outcome.value);
      } else {
        stillFailed.push(datesToRetry[idx]);
      }
    });

    setDailyDataMap(newMap);
    setMissingDates(stillFailed);
    if (newMap.size > 0) {
      setError(null);
    }
    calculatePeriodMetrics(Array.from(newMap.values()));
    setLoading(false);
  };

  const calculatePeriodMetrics = (dailyData: DailyData[]) => {
    if (dailyData.length === 0) {
      setPeriodMetrics(null);
      return;
    }

    let totalPnL = 0;
    let totalRealizedPnL = 0;
    let winningDays = 0;
    let losingDays = 0;
    let bestDay: { date: string; pnl: number } | null = null;
    let worstDay: { date: string; pnl: number } | null = null;
    const symbolPerformance: { [symbol: string]: { trades: number; pnl: number } } = {};

    // Sort dailyData by date to ensure we get the earliest date first
    const sortedDailyData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));

    // Get starting equity from the EARLIEST day's account data
    const firstDay = sortedDailyData[0];
    const firstDayPnL = firstDay.actual_daily_pnl ?? firstDay.eodData.realized_pnl_day_from_pairs;
    const startingEquity = firstDay.eodData.account.equity - firstDayPnL;

    dailyData.forEach((day) => {
      const dailyPnL = day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs;
      const realizedPnL = day.eodData.realized_pnl_day_from_pairs;

      totalPnL += dailyPnL;
      totalRealizedPnL += realizedPnL;

      if (dailyPnL > 0) winningDays++;
      if (dailyPnL < 0) losingDays++;

      if (!bestDay || dailyPnL > bestDay.pnl) {
        bestDay = { date: day.date, pnl: dailyPnL };
      }
      if (!worstDay || dailyPnL < worstDay.pnl) {
        worstDay = { date: day.date, pnl: dailyPnL };
      }

      // Aggregate symbol performance
      Object.entries(day.eodData.per_symbol_totals).forEach(([symbol, data]) => {
        if (!symbolPerformance[symbol]) {
          symbolPerformance[symbol] = { trades: 0, pnl: 0 };
        }
        symbolPerformance[symbol].trades += data.trades;
        symbolPerformance[symbol].pnl += data.realized_pnl;
      });
    });

    const avgDailyPnL = totalPnL / dailyData.length;
    const winRate = winningDays / (winningDays + losingDays) * 100;
    const totalPnLPercent = startingEquity > 0 ? (totalPnL / startingEquity) * 100 : 0;

    setPeriodMetrics({
      totalPnL,
      totalRealizedPnL,
      avgDailyPnL,
      bestDay,
      worstDay,
      winningDays,
      losingDays,
      winRate,
      symbolPerformance,
      totalPnLPercent,
      startingEquity,
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDateTime = (dateTimeStr: string) => {
    try {
      const dt = new Date(dateTimeStr);
      return dt.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'America/New_York'  // Always display in EST/EDT
      });
    } catch {
      return dateTimeStr;
    }
  };

  const toggleDayExpansion = (date: string) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDays(newExpanded);
  };


  const exportToPDF = () => {
    if (!periodMetrics || dailyDataMap.size === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${displayName} EOD Report`, pageWidth / 2, 15, { align: 'center' });
    
    // Period
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${startDate} to ${endDate} (${dailyDataMap.size} trading days)`, pageWidth / 2, 22, { align: 'center' });
    
    let yPos = 30;

    // Summary Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Period Summary', 14, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const summaryData = [
      ['Total P&L', formatCurrency(periodMetrics.totalPnL)],
      ['Total P&L %', `${periodMetrics.totalPnL >= 0 ? '+' : ''}${periodMetrics.totalPnLPercent.toFixed(2)}%`],
      ['Starting Equity', formatCurrency(periodMetrics.startingEquity)],
      ['Total Realized P&L', formatCurrency(periodMetrics.totalRealizedPnL)],
      ['Average Daily P&L', formatCurrency(periodMetrics.avgDailyPnL)],
      ['Win Rate', `${periodMetrics.winRate.toFixed(2)}%`],
      ['Winning Days', periodMetrics.winningDays.toString()],
      ['Losing Days', periodMetrics.losingDays.toString()],
      ['Best Day', periodMetrics.bestDay ? `${periodMetrics.bestDay.date}: ${formatCurrency(periodMetrics.bestDay.pnl)}` : 'N/A'],
      ['Worst Day', periodMetrics.worstDay ? `${periodMetrics.worstDay.date}: ${formatCurrency(periodMetrics.worstDay.pnl)}` : 'N/A'],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [103, 126, 234] },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // Daily Performance Table with Percentages
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Daily Performance', 14, yPos);
    yPos += 8;

    const sortedDates = Array.from(dailyDataMap.keys()).sort();
    const dailyRows = sortedDates.map((date) => {
      const day = dailyDataMap.get(date);
      if (!day) return [];
      
      const dailyPnL = day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs;
      const realizedPnL = day.eodData.realized_pnl_day_from_pairs;
      const winners = day.eodData.pairs.filter(p => p.realized_pnl > 0).length;
      const losers = day.eodData.pairs.filter(p => p.realized_pnl < 0).length;
      const dayWinRate = day.eodData.num_pairs > 0 ? ((winners / day.eodData.num_pairs) * 100).toFixed(1) : '0.0';
      
      // Calculate daily percentage based on that day's starting equity
      const endingEquity = day.eodData.account.equity || 0;
      const startingEquityForThisDay = endingEquity - dailyPnL;
      const dailyPercent = startingEquityForThisDay > 0 ? ((dailyPnL / startingEquityForThisDay) * 100).toFixed(2) : '0.00';
      
      return [
        date,
        formatCurrency(dailyPnL),
        `${dailyPnL >= 0 ? '+' : ''}${dailyPercent}%`,
        day.eodData.num_pairs.toString(),
        `${winners}/${losers}`,
        `${dayWinRate}%`,
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Daily P&L', '%', 'Pairs', 'W/L', 'Win Rate']],
      body: dailyRows,
      theme: 'striped',
      headStyles: { fillColor: [103, 126, 234] },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        // Color the P&L and percentage cells
        if ((data.column.index === 1 || data.column.index === 2) && data.section === 'body') {
          const value = data.cell.raw as string;
          if (value && (value.includes('-') || value.startsWith('-'))) {
            data.cell.styles.textColor = [211, 47, 47]; // Red
          } else if (value && (value.includes('$') || value.includes('%'))) {
            data.cell.styles.textColor = [46, 125, 50]; // Green
          }
        }
      },
    });

    yPos = (doc as any).lastAutoTable.finalY + 10;

    // Symbol Performance - Today vs Period
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Symbol Performance (Today vs Period)', 14, yPos);
    yPos += 8;

    // Get today's data (most recent day)
    const todayDate = sortedDates[sortedDates.length - 1];
    const todayData = dailyDataMap.get(todayDate);
    const todaySymbols = todayData?.eodData.per_symbol_totals || {};

    // Get all symbols
    const allSymbols = new Set([
      ...Object.keys(todaySymbols),
      ...Object.keys(periodMetrics.symbolPerformance)
    ]);

    // Create combined symbol data
    const symbolCombinedData = Array.from(allSymbols).map(symbol => ({
      symbol,
      todayPnl: todaySymbols[symbol]?.realized_pnl || 0,
      todayTrades: todaySymbols[symbol]?.trades || 0,
      periodPnl: periodMetrics.symbolPerformance[symbol]?.pnl || 0,
      periodTrades: periodMetrics.symbolPerformance[symbol]?.trades || 0,
      tradedToday: (todaySymbols[symbol]?.trades || 0) > 0,
    }));

    // Sort: Traded today first, then by period P&L
    symbolCombinedData.sort((a, b) => {
      if (a.tradedToday && !b.tradedToday) return -1;
      if (!a.tradedToday && b.tradedToday) return 1;
      if (a.tradedToday && b.tradedToday) return b.todayPnl - a.todayPnl;
      return b.periodPnl - a.periodPnl;
    });

    const symbolRows = symbolCombinedData.slice(0, 10).map(data => [
      data.symbol,
      data.todayPnl !== 0 ? formatCurrency(data.todayPnl) : '-',
      data.todayTrades > 0 ? data.todayTrades.toString() : '-',
      formatCurrency(data.periodPnl),
      data.periodTrades.toString(),
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Symbol', 'Today P&L', 'Today Trades', 'Period P&L', 'Period Trades']],
      body: symbolRows,
      theme: 'grid',
      headStyles: { fillColor: [103, 126, 234] },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        // Color the P&L columns
        if ((data.column.index === 1 || data.column.index === 3) && data.section === 'body') {
          const value = data.cell.raw as string;
          if (value && value.includes('-')) {
            data.cell.styles.textColor = [211, 47, 47]; // Red
          } else if (value && value.includes('$')) {
            data.cell.styles.textColor = [46, 125, 50]; // Green
          }
        }
      },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Add Daily P&L Chart
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Daily P&L Chart', 14, yPos);
    yPos += 10;

    // Prepare chart data
    const chartWidth = pageWidth - 28; // margins
    const chartHeight = 80;
    const chartX = 14;
    const chartY = yPos;

    // Get max/min values for scaling
    const dailyPnLValues = sortedDates.map((date) => {
      const day = dailyDataMap.get(date);
      return day ? (day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs) : 0;
    });
    const maxPnL = Math.max(...dailyPnLValues, 0);
    const minPnL = Math.min(...dailyPnLValues, 0);
    const pnlRange = maxPnL - minPnL;

    // Draw axes
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    // X-axis
    const zeroLine = chartY + chartHeight * (maxPnL / pnlRange);
    doc.line(chartX, zeroLine, chartX + chartWidth, zeroLine);
    // Y-axis
    doc.line(chartX, chartY, chartX, chartY + chartHeight);

    // Draw bars
    const barWidth = chartWidth / sortedDates.length - 2;
    sortedDates.forEach((date, index) => {
      const day = dailyDataMap.get(date);
      if (!day) return;
      
      const dailyPnL = day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs;
      const barHeight = Math.abs(dailyPnL) / pnlRange * chartHeight;
      const barX = chartX + (index * chartWidth / sortedDates.length) + 1;
      const barY = dailyPnL >= 0 ? zeroLine - barHeight : zeroLine;
      
      // Set color based on positive/negative
      if (dailyPnL >= 0) {
        doc.setFillColor(46, 125, 50); // Green
      } else {
        doc.setFillColor(211, 47, 47); // Red
      }
      
      doc.rect(barX, barY, barWidth, barHeight, 'F');
    });

    // Add axis labels
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    
    // Max value label
    doc.text(`$${maxPnL.toFixed(0)}`, chartX - 2, chartY + 4, { align: 'right' });
    // Zero line label
    doc.text('$0', chartX - 2, zeroLine + 2, { align: 'right' });
    // Min value label
    if (minPnL < 0) {
      doc.text(`$${minPnL.toFixed(0)}`, chartX - 2, chartY + chartHeight + 2, { align: 'right' });
    }

    // Add date labels (first, middle, last)
    doc.text(sortedDates[0].substring(5), chartX, chartY + chartHeight + 8);
    if (sortedDates.length > 2) {
      const midIndex = Math.floor(sortedDates.length / 2);
      doc.text(sortedDates[midIndex].substring(5), chartX + chartWidth / 2, chartY + chartHeight + 8, { align: 'center' });
    }
    doc.text(sortedDates[sortedDates.length - 1].substring(5), chartX + chartWidth, chartY + chartHeight + 8, { align: 'right' });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Save PDF
    doc.save(`${displayName}_${startDate}_to_${endDate}.pdf`);
    setExportMenuAnchor(null);
  };

  // Prepare chart data
  const chartData = Array.from(dailyDataMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, day]) => {
      const dailyPnL = day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs;
      return {
        date: date.substring(5), // MM-DD format
        fullDate: date,
        dailyPnL: parseFloat(dailyPnL.toFixed(2)),
        realizedPnL: parseFloat((day.eodData.realized_pnl_day_from_pairs ?? 0).toFixed(2)),
      };
    });

  // Calculate cumulative P&L and percentage
  // Each day's percentage should be calculated based on that day's starting equity, not the period's starting equity
  let cumulative = 0;
  const chartDataWithCumulative = chartData.map((d) => {
    cumulative += d.dailyPnL;
    
    // Get the actual account equity for this specific day from the dailyDataMap
    const dayData = dailyDataMap.get(d.fullDate);
    const endingEquity = dayData?.eodData.account.equity || 0;
    const startingEquityForThisDay = endingEquity - d.dailyPnL; // Starting equity = ending equity - daily P&L
    
    // Calculate percentage based on THIS day's starting equity, not the period's starting equity
    const percentage = startingEquityForThisDay > 0 ? (d.dailyPnL / startingEquityForThisDay) * 100 : 0;
    
    return { 
      ...d, 
      cumulativePnL: parseFloat(cumulative.toFixed(2)),
      percentage: parseFloat(percentage.toFixed(2))
    };
  });

  // ----- CSP weekly-wheel detection & period anchor -----
  // Wheel EOD files embed full period analytics (period.*, daily_series,
  // per_symbol_period, wheel_state, ...), so period-level panels are driven by
  // the most recent day in range rather than aggregating across the range.
  const sortedDaysAsc = Array.from(dailyDataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  const wheelAnchor: EODData | null = sortedDaysAsc.length > 0 ? sortedDaysAsc[sortedDaysAsc.length - 1].eodData : null;
  const isWheel = !!(wheelAnchor && (wheelAnchor.period || wheelAnchor.daily_series || wheelAnchor.wheel_state));
  // v2 wheel scoreboard blocks (fall back to legacy period/cash_locked when absent)
  const wPerf = wheelAnchor?.performance;
  const wPnl = wheelAnchor?.pnl_summary;
  const wCap = wheelAnchor?.capital;
  const wPerfToday = wPerf?.today;
  const wPerfWtd = wPerf?.week_to_date;
  const wPerfCum = wPerf?.cumulative;
  const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
  const fmtWinRate = (wr: number | null | undefined) =>
    wr === null || wr === undefined ? '—' : `${(wr * 100).toFixed(0)}%`;

  // ---- Spread-centric PCS (Put Credit Spread) detection + aggregation ----
  // The open book, win-rate, closed-trade log and since-inception roll-ups are
  // point-in-time, so they come from the LATEST file in range (pcsAnchor). The
  // premium-collected / realized-P&L / daily-P&L series are per-day flows, so we
  // sum (or chain) them across every file in range.
  const isPcs = !!(wheelAnchor && (String(wheelAnchor.strategy || '').toLowerCase() === 'pcs' || wheelAnchor.open_spreads));
  const pcsAnchor: EODData | null = isPcs ? wheelAnchor : null;
  const pcsSummary = pcsAnchor?.summary;
  let pcsPremiumPeriod = 0;
  let pcsRealizedPeriod = 0;
  const pcsDailySeries: { date: string; daily: number; cumulative: number }[] = [];
  const pcsEntriesPeriod: OpenSpread[] = [];
  const pcsExitsPeriod: ClosedSpreadTrade[] = [];
  if (isPcs) {
    let running = 0;
    sortedDaysAsc.forEach(d => {
      const s = d.eodData.summary;
      if (!s) return;
      pcsPremiumPeriod += s.premium_collected_today || 0;
      pcsRealizedPeriod += s.realized_pnl_today || 0;
      running += s.day_pnl_estimate || 0;
      pcsDailySeries.push({ date: d.date, daily: s.day_pnl_estimate || 0, cumulative: running });
      ((d.eodData.entries_today as unknown as OpenSpread[]) || []).forEach(e => pcsEntriesPeriod.push(e));
      ((d.eodData.exits_today as unknown as ClosedSpreadTrade[]) || []).forEach(x => pcsExitsPeriod.push(x));
    });
  }
  const fmtSpreadStrikes = (s: { short_strike: number; long_strike: number }) =>
    `${s.short_strike % 1 === 0 ? s.short_strike : s.short_strike}/${s.long_strike % 1 === 0 ? s.long_strike : s.long_strike}p`;
  const pcsProfitTarget = pcsAnchor?.config?.profit_close_pct ?? 0.5;
  const pcsMaxLossLimit = pcsAnchor?.config?.max_loss_close_pct ?? 0.8;

  // ---- CSP Wheel SCHEMA v2 (E-OPTIONS) detection + aggregation ----
  // Hold-to-expiry wheel. Point-in-time blocks (account, open_positions,
  // open_book_summary, lifetime, closed_trades) come from the LATEST file in
  // range. Per-day flows (premium/realized/entries/exits) are summed across the
  // range, and the cumulative P&L curve chains day_pnl_true (equity-last_equity).
  const isCspWheelV2 = !!(
    wheelAnchor &&
    !isWheel &&
    !isPcs &&
    ((wheelAnchor.schema_version === 2 && String(wheelAnchor.strategy || '').toLowerCase() === 'csp_wheel') ||
      (wheelAnchor.open_book_summary && wheelAnchor.lifetime))
  );
  const v2Anchor: EODData | null = isCspWheelV2 ? wheelAnchor : null;
  const v2Book = v2Anchor?.open_book_summary;
  const v2Life = v2Anchor?.lifetime;
  let v2PremiumPeriod = 0;
  let v2PremiumPaidPeriod = 0;
  let v2RealizedPeriod = 0;
  const v2DailySeries: { date: string; daily: number; cumulative: number }[] = [];
  const v2EntriesPeriod: WheelEntryToday[] = [];
  const v2ExitsPeriod: WheelClosedTrade[] = [];
  if (isCspWheelV2) {
    let running = 0;
    sortedDaysAsc.forEach(d => {
      const e = d.eodData;
      v2PremiumPeriod += e.premium_collected_today || 0;
      v2PremiumPaidPeriod += e.premium_paid_today || 0;
      v2RealizedPeriod += e.realized_pnl_today || 0;
      const dayTrue = e.day_pnl_true ?? d.actual_daily_pnl ?? 0;
      running += dayTrue;
      v2DailySeries.push({ date: d.date, daily: dayTrue, cumulative: running });
      ((e.entries_today as unknown as WheelEntryToday[]) || []).forEach(x => v2EntriesPeriod.push(x));
      ((e.exits_today as unknown as WheelClosedTrade[]) || []).forEach(x => v2ExitsPeriod.push(x));
    });
  }
  // Closed trades from the latest file, scoped to the selected date range by closed_at.
  const v2ClosedInRange: WheelClosedTrade[] = isCspWheelV2
    ? ((v2Anchor!.closed_trades as unknown as WheelClosedTrade[]) || []).filter(t => {
        const cd = (t.closed_at || '').substring(0, 10);
        return cd >= startDate && cd <= endDate;
      })
    : [];
  const fmtRoP = (v: number | null | undefined) =>
    v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`;
  // Exit-reason color: profit_close green, max_loss_stop red, else neutral by sign.
  const reasonColor = (category: string, pnl: number): string => {
    const c = (category || '').toLowerCase();
    if (c.includes('profit')) return '#4caf50';
    if (c.includes('max_loss') || c.includes('stop')) return '#f44336';
    if (c.includes('assign')) return '#ff9800';
    return pnl >= 0 ? '#4caf50' : '#f44336';
  };

  if (availableDates.length === 0 && !loading) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          No EOD Data Available
        </Typography>
        <Typography variant="body2">
          This instance does not have any End of Day (EOD) reports available.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Date Range Selector */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon color="primary" />
              <Typography variant="h6" sx={{ display: { xs: 'none', sm: 'block' } }}>Date Range</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
              <TextField
                select
                label="Start Date"
                value={startDate}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartDate(newStart);
                  // If new start date is after end date, adjust end date
                  if (newStart > endDate) {
                    setEndDate(newStart);
                  }
                }}
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', sm: 150 } }}
              >
                {availableDates.map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: { xs: '100%', sm: 150 } }}
              >
                {availableDates.filter(d => d >= startDate).map((date) => (
                  <MenuItem key={date} value={date}>
                    {date}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                {availableDates.length} days available
              </Typography>
              {periodMetrics && !isWheel && !isPcs && !isCspWheelV2 && (
                <>
                  <Tooltip title="Export Report">
                    <IconButton 
                      onClick={(e) => setExportMenuAnchor(e.currentTarget)} 
                      size="small" 
                      color="primary"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                  >
                    <MenuItem onClick={exportToPDF}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Export as PDF</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Missing days notice - non-blocking, shown alongside whatever data loaded */}
      {!loading && missingDates.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retryMissingDates}>
              Retry
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {missingDates.length} day{missingDates.length !== 1 ? 's' : ''} could not be loaded
          </Typography>
          <Typography variant="caption">
            Missing: {[...missingDates].sort().join(', ')}
          </Typography>
        </Alert>
      )}

      {/* ============ CSP WHEEL v2 VIEW (hold-to-expiry) ============ */}
      {!loading && !error && isCspWheelV2 && v2Anchor && v2Book && v2Life && (
        <>
          {/* KPI header */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: (v2Life.realized_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Lifetime Realized P&L</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: (v2Life.realized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(v2Life.realized_pnl ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    avg {formatCurrency(v2Life.avg_realized_per_trade ?? 0)}/trade
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Win Rate</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: (v2Life.win_rate_pct ?? 0) >= 50 ? '#4caf50' : '#f44336' }}>
                    {(v2Life.win_rate_pct ?? 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    of {v2Life.num_closed_trades ?? 0} closed · {v2Life.wins ?? 0}W/{v2Life.losses ?? 0}L
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: (v2Book.total_unrealized_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)', height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Open Unrealized</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: (v2Book.total_unrealized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(v2Book.total_unrealized_pnl ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    today {(v2Book.total_unrealized_intraday_pnl ?? 0) >= 0 ? '+' : ''}{formatCurrency(v2Book.total_unrealized_intraday_pnl ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Premium Collected</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {formatCurrency(v2PremiumPeriod)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    period · lifetime {formatCurrency(v2Life.gross_premium_collected ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Collateral Utilization</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: (v2Book.collateral_utilization_pct ?? 0) >= 80 ? '#f44336' : '#1976d2' }}>
                    {(v2Book.collateral_utilization_pct ?? 0).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(v2Book.total_collateral ?? 0)} locked
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption" color="text.secondary">Avg Days Held</Typography>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold' }}>
                    {(v2Life.avg_days_held ?? 0).toFixed(1)}d
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {v2Book.num_positions ?? 0} open position{(v2Book.num_positions ?? 0) !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cumulative P&L curve from day_pnl_true */}
          {v2DailySeries.length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Daily P&L (true daily mark) & Cumulative
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 260 : 360}>
                <ComposedChart data={v2DailySeries.map(p => ({ date: p.date.substring(5), daily: parseFloat(p.daily.toFixed(2)), cumulative: parseFloat(p.cumulative.toFixed(2)) }))} margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="daily" name="Day P&L (true)">
                    {v2DailySeries.map((entry, index) => (
                      <Cell key={`v2-cell-${index}`} fill={entry.daily >= 0 ? '#4caf50' : '#f44336'} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative" stroke="#1976d2" strokeWidth={2} dot={{ r: isMobile ? 3 : 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* P&L by Exit Reason */}
          {Object.keys(v2Life.by_reason || {}).length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
              <Typography variant="h6" gutterBottom>P&L by Exit Reason (lifetime)</Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <ComposedChart
                  data={Object.entries(v2Life.by_reason).map(([reason, v]) => ({ reason: reason.replace(/_/g, ' '), pnl: parseFloat((v.realized_pnl ?? 0).toFixed(2)), count: v.count ?? 0 }))}
                  margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="reason" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <YAxis tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <RechartsTooltip
                    formatter={(value: number, name: string) => name === 'pnl' ? formatCurrency(value) : value}
                    contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }}
                  />
                  <Bar dataKey="pnl" name="Realized P&L">
                    {Object.entries(v2Life.by_reason).map(([reason, v], index) => (
                      <Cell key={`reason-${index}`} fill={(v.realized_pnl ?? 0) >= 0 ? '#4caf50' : '#f44336'} />
                    ))}
                    <LabelList dataKey="count" position="top" formatter={(v: number) => `${v}x`} style={{ fontSize: isMobile ? 9 : 11, fill: '#ffffff' }} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {v2Life.best_trade && (
                  <Chip size="small" color="success" variant="outlined"
                    label={`Best: ${v2Life.best_trade.underlying} ${v2Life.best_trade.strike}${(v2Life.best_trade.option_type || '').charAt(0)} ${formatCurrency(v2Life.best_trade.realized_pnl)}`} />
                )}
                {v2Life.worst_trade && (
                  <Chip size="small" color="error" variant="outlined"
                    label={`Worst: ${v2Life.worst_trade.underlying} ${v2Life.worst_trade.strike}${(v2Life.worst_trade.option_type || '').charAt(0)} ${formatCurrency(v2Life.worst_trade.realized_pnl)}`} />
                )}
              </Box>
            </Paper>
          )}

          {/* Open Positions */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Open Positions ({(v2Anchor.open_positions || []).length})
            </Typography>
            {(v2Anchor.open_positions || []).length === 0 ? (
              <Alert severity="info">No open positions.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Underlying</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Strike</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell align="right">DTE</TableCell>
                      <TableCell align="right">Held</TableCell>
                      <TableCell align="right">Premium</TableCell>
                      <TableCell align="right">Collateral</TableCell>
                      <TableCell align="right">Unrealized</TableCell>
                      <TableCell align="right">RoP %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...(v2Anchor.open_positions || [])]
                      .sort((a, b) => (a.dte ?? 0) - (b.dte ?? 0))
                      .map((p, idx) => (
                        <TableRow key={`${p.symbol || p.underlying}-${idx}`} sx={{ bgcolor: (p.unrealized_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.06)' : 'rgba(244, 67, 54, 0.06)' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{p.underlying}</TableCell>
                          <TableCell>{p.option_type}</TableCell>
                          <TableCell align="right">{p.strike}</TableCell>
                          <TableCell>{p.expiry}</TableCell>
                          <TableCell align="right">{p.dte}</TableCell>
                          <TableCell align="right">{p.days_held ?? 0}d</TableCell>
                          <TableCell align="right" sx={{ color: '#2e7d32' }}>{formatCurrency(p.premium_collected ?? 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.collateral_locked ?? 0)}</TableCell>
                          <TableCell align="right" sx={{ color: (p.unrealized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>{formatCurrency(p.unrealized_pnl ?? 0)}</TableCell>
                          <TableCell align="right" sx={{ color: (p.return_on_premium_pct ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{fmtRoP(p.return_on_premium_pct)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Closed Trades (scoped to date range by closed_at) */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Closed Trades — {startDate} to {endDate} ({v2ClosedInRange.length})
            </Typography>
            {v2ClosedInRange.length === 0 ? (
              <Alert severity="info">No trades closed in this date range.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Underlying</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Strike</TableCell>
                      <TableCell>Opened</TableCell>
                      <TableCell>Closed</TableCell>
                      <TableCell align="right">Held</TableCell>
                      <TableCell align="right">Entry Premium</TableCell>
                      <TableCell align="right">Realized</TableCell>
                      <TableCell align="right">RoP %</TableCell>
                      <TableCell>Close Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...v2ClosedInRange]
                      .sort((a, b) => (b.closed_at || '').localeCompare(a.closed_at || ''))
                      .map((t, idx) => (
                        <TableRow key={`v2closed-${idx}`} sx={{ bgcolor: `${reasonColor(t.reason_category, t.realized_pnl ?? 0)}14` }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t.underlying}</TableCell>
                          <TableCell>{t.option_type}</TableCell>
                          <TableCell align="right">{t.strike}</TableCell>
                          <TableCell>{(t.opened_at || '').substring(0, 10)}</TableCell>
                          <TableCell>{(t.closed_at || '').substring(0, 10)}</TableCell>
                          <TableCell align="right">{t.days_held ?? 0}d</TableCell>
                          <TableCell align="right">{formatCurrency(t.entry_premium ?? 0)}</TableCell>
                          <TableCell align="right" sx={{ color: (t.realized_pnl ?? 0) > 0 ? '#2e7d32' : (t.realized_pnl ?? 0) < 0 ? '#c62828' : 'text.secondary', fontWeight: 'bold' }}>
                            {formatCurrency(t.realized_pnl ?? 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: (t.return_on_premium_pct ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>{fmtRoP(t.return_on_premium_pct)}</TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={(t.close_reason || t.reason_category || '').replace(/_/g, ' ')}
                              sx={{ borderColor: reasonColor(t.reason_category, t.realized_pnl ?? 0), color: reasonColor(t.reason_category, t.realized_pnl ?? 0) }} />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Activity feed: entries / exits over the range */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity ({v2EntriesPeriod.length} opened · {v2ExitsPeriod.length} closed)
            </Typography>
            {v2EntriesPeriod.length === 0 && v2ExitsPeriod.length === 0 ? (
              <Alert severity="info">
                No entries or exits in range — {v2Book.num_positions ?? 0} position{(v2Book.num_positions ?? 0) !== 1 ? 's' : ''} held.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {v2EntriesPeriod.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Opened (SELL_TO_OPEN)</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Underlying</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Strike</TableCell>
                            <TableCell align="right">DTE</TableCell>
                            <TableCell align="right">Premium</TableCell>
                            <TableCell align="right">Collateral</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {v2EntriesPeriod.map((e, i) => (
                            <TableRow key={`v2entry-${i}`}>
                              <TableCell>{e.underlying}</TableCell>
                              <TableCell>{e.option_type}</TableCell>
                              <TableCell align="right">{e.strike}</TableCell>
                              <TableCell align="right">{e.dte_at_entry ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ color: '#2e7d32' }}>{formatCurrency(e.premium_collected ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(e.collateral_locked ?? 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
                {v2ExitsPeriod.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Closed (BUY_TO_CLOSE / expiry / assignment)</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Underlying</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Strike</TableCell>
                            <TableCell align="right">Held</TableCell>
                            <TableCell align="right">Realized</TableCell>
                            <TableCell>Reason</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {v2ExitsPeriod.map((x, i) => (
                            <TableRow key={`v2exit-${i}`}>
                              <TableCell>{x.underlying}</TableCell>
                              <TableCell>{x.option_type}</TableCell>
                              <TableCell align="right">{x.strike}</TableCell>
                              <TableCell align="right">{x.days_held ?? 0}d</TableCell>
                              <TableCell align="right" sx={{ color: (x.realized_pnl ?? 0) > 0 ? '#2e7d32' : (x.realized_pnl ?? 0) < 0 ? '#c62828' : 'text.secondary', fontWeight: 'bold' }}>
                                {formatCurrency(x.realized_pnl ?? 0)}
                              </TableCell>
                              <TableCell>
                                <Chip size="small" variant="outlined" label={(x.close_reason || x.reason_category || '').replace(/_/g, ' ')}
                                  sx={{ borderColor: reasonColor(x.reason_category, x.realized_pnl ?? 0), color: reasonColor(x.reason_category, x.realized_pnl ?? 0) }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </>
      )}

      {/* ============ PUT CREDIT SPREAD (PCS) VIEW ============ */}
      {!loading && !error && isPcs && pcsAnchor && pcsSummary && (
        <>
          {/* Header KPIs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.12)', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Net Premium Working</Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {formatCurrency(pcsSummary.open_credit_at_risk ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pcsSummary.num_open_spreads ?? 0} open spread{(pcsSummary.num_open_spreads ?? 0) !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: (pcsSummary.open_unrealized_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Open Unrealized P&L</Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: (pcsSummary.open_unrealized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(pcsSummary.open_unrealized_pnl ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    today {(pcsSummary.open_unrealized_intraday_pnl ?? 0) >= 0 ? '+' : ''}{formatCurrency(pcsSummary.open_unrealized_intraday_pnl ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: pcsRealizedPeriod >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Realized P&L (period)</Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: pcsRealizedPeriod >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(pcsRealizedPeriod)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    cumulative {formatCurrency(pcsSummary.realized_pnl_all_time ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.12)', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Capital at Risk</Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#e65100' }}>
                    {formatCurrency(pcsSummary.open_max_loss_total ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pcsAnchor.account.equity > 0 ? `${(((pcsSummary.open_max_loss_total ?? 0) / pcsAnchor.account.equity) * 100).toFixed(1)}% of equity` : '—'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Secondary KPIs */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Closed-Trade Win Rate</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: (pcsSummary.win_rate ?? 0) >= 0.5 ? '#4caf50' : '#f44336' }}>
                    {fmtWinRate(pcsSummary.win_rate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pcsSummary.num_wins ?? 0}W / {pcsSummary.num_losses ?? 0}L · {pcsSummary.num_closed_all_time ?? 0} closed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Premium Collected (period)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {formatCurrency(pcsPremiumPeriod)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    all-time {formatCurrency(pcsSummary.premium_collected_all_time ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Premium Working</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                    {formatCurrency(pcsSummary.open_credit_at_risk ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    cash locked {formatCurrency(pcsSummary.open_cash_locked ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: (pcsSummary.day_pnl_estimate ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.12)', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Day P&L (MTM)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: (pcsSummary.day_pnl_estimate ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(pcsSummary.day_pnl_estimate ?? 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pcsAnchor.date_et} · realized {formatCurrency(pcsSummary.realized_pnl_today ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Daily P&L & Cumulative chart */}
          {pcsDailySeries.length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Daily P&L (mark-to-market) & Cumulative
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 260 : 340}>
                <ComposedChart data={pcsDailySeries.map(p => ({ date: p.date.substring(5), daily: parseFloat(p.daily.toFixed(2)), cumulative: parseFloat(p.cumulative.toFixed(2)) }))} margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 9 : 11, fill: '#ccc' }} />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="daily" name="Day P&L (MTM)">
                    {pcsDailySeries.map((entry, index) => (
                      <Cell key={`pcs-cell-${index}`} fill={entry.daily >= 0 ? '#4caf50' : '#f44336'} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative" stroke="#1976d2" strokeWidth={2} dot={{ r: isMobile ? 3 : 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Open Spreads — the centerpiece */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">
                Open Spreads ({(pcsAnchor.open_spreads || []).length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                profit-close at {(pcsProfitTarget * 100).toFixed(0)}% credit · hard-stop at {(pcsMaxLossLimit * 100).toFixed(0)}% max-loss
              </Typography>
            </Box>
            {(pcsAnchor.open_spreads || []).length === 0 ? (
              <Alert severity="info">No open spreads.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Underlying</TableCell>
                      <TableCell>Strikes</TableCell>
                      <TableCell align="right">DTE</TableCell>
                      <TableCell align="right">Held</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Cost to Close</TableCell>
                      <TableCell align="right">Unrealized</TableCell>
                      <TableCell align="right">Max Loss</TableCell>
                      <TableCell>% Credit Captured</TableCell>
                      <TableCell>% Max Loss Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...(pcsAnchor.open_spreads || [])]
                      .sort((a, b) => (b.pct_of_max_loss_used ?? 0) - (a.pct_of_max_loss_used ?? 0))
                      .map((sp, idx) => {
                        const capturedReady = (sp.pct_of_credit_captured ?? 0) >= pcsProfitTarget;
                        const lossDanger = (sp.pct_of_max_loss_used ?? 0) >= pcsMaxLossLimit;
                        const rowBg = lossDanger
                          ? 'rgba(244, 67, 54, 0.18)'
                          : capturedReady
                            ? 'rgba(76, 175, 80, 0.18)'
                            : (sp.unrealized_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.06)' : 'rgba(244, 67, 54, 0.06)';
                        const capturedPct = Math.max(0, Math.min(100, ((sp.pct_of_credit_captured ?? 0) / (pcsProfitTarget || 1)) * 100));
                        const lossPct = Math.max(0, Math.min(100, ((sp.pct_of_max_loss_used ?? 0) / (pcsMaxLossLimit || 1)) * 100));
                        return (
                          <TableRow key={`${sp.underlying}-${idx}`} sx={{ bgcolor: rowBg }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{sp.underlying}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sp.contracts}x · exp {sp.expiry}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{fmtSpreadStrikes(sp)}</Typography>
                              <Typography variant="caption" color="text.secondary">w {sp.width}</Typography>
                            </TableCell>
                            <TableCell align="right">{sp.dte}</TableCell>
                            <TableCell align="right">{sp.days_held}d</TableCell>
                            <TableCell align="right">{formatCurrency(sp.entry_credit_total ?? 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(sp.current_cost_to_close_total ?? 0)}</TableCell>
                            <TableCell align="right" sx={{ color: (sp.unrealized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>
                              {formatCurrency(sp.unrealized_pnl ?? 0)}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(sp.max_loss_total ?? 0)}</TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <Typography variant="caption" sx={{ color: capturedReady ? '#2e7d32' : 'text.secondary' }}>
                                {((sp.pct_of_credit_captured ?? 0) * 100).toFixed(0)}%{capturedReady ? ' ✓' : ''}
                              </Typography>
                              <LinearProgress variant="determinate" value={capturedPct} sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: capturedReady ? '#4caf50' : '#90caf9' } }} />
                            </TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <Typography variant="caption" sx={{ color: lossDanger ? '#c62828' : 'text.secondary' }}>
                                {((sp.pct_of_max_loss_used ?? 0) * 100).toFixed(0)}%{lossDanger ? ' ⚠' : ''}
                              </Typography>
                              <LinearProgress variant="determinate" value={lossPct} sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { backgroundColor: lossDanger ? '#f44336' : (sp.pct_of_max_loss_used ?? 0) >= pcsMaxLossLimit * 0.6 ? '#ff9800' : '#a5d6a7' } }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Activity: entries / exits over the selected range */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Activity ({pcsEntriesPeriod.length} opened · {pcsExitsPeriod.length} closed)
            </Typography>
            {pcsEntriesPeriod.length === 0 && pcsExitsPeriod.length === 0 ? (
              <Alert severity="info">
                No entries or exits in range — {pcsSummary.num_open_spreads ?? 0} spread{(pcsSummary.num_open_spreads ?? 0) !== 1 ? 's' : ''} held.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {pcsEntriesPeriod.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Opened</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Underlying</TableCell>
                            <TableCell>Strikes</TableCell>
                            <TableCell align="right">DTE</TableCell>
                            <TableCell align="right">Credit</TableCell>
                            <TableCell align="right">Max Loss</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pcsEntriesPeriod.map((e, i) => (
                            <TableRow key={`entry-${i}`}>
                              <TableCell>{e.underlying}</TableCell>
                              <TableCell>{fmtSpreadStrikes(e)}</TableCell>
                              <TableCell align="right">{e.dte}</TableCell>
                              <TableCell align="right" sx={{ color: '#2e7d32' }}>{formatCurrency(e.entry_credit_total ?? 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(e.max_loss_total ?? 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
                {pcsExitsPeriod.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Closed</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Underlying</TableCell>
                            <TableCell>Strikes</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Held</TableCell>
                            <TableCell align="right">Realized</TableCell>
                            <TableCell align="right">% Cap</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pcsExitsPeriod.map((x, i) => (
                            <TableRow key={`exit-${i}`}>
                              <TableCell>{x.underlying}</TableCell>
                              <TableCell>{fmtSpreadStrikes(x)}</TableCell>
                              <TableCell><Chip size="small" variant="outlined" label={(x.exit_reason || x.exit_kind || '').replace(/_/g, ' ')} /></TableCell>
                              <TableCell align="right">{(x.hold_days ?? 0).toFixed(1)}d</TableCell>
                              <TableCell align="right" sx={{ color: (x.realized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>{formatCurrency(x.realized_pnl ?? 0)}</TableCell>
                              <TableCell align="right">{((x.pct_of_credit_captured ?? 0) * 100).toFixed(0)}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>

          {/* Closed Trades / Trade Log (since inception) */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Closed Trades — Trade Log ({(pcsAnchor.closed_trades || []).length})
            </Typography>
            {(pcsAnchor.closed_trades || []).length === 0 ? (
              <Alert severity="info">No closed trades yet.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Underlying</TableCell>
                      <TableCell>Strikes</TableCell>
                      <TableCell>Opened</TableCell>
                      <TableCell>Closed</TableCell>
                      <TableCell align="right">Held</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Realized</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">% Cap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...(pcsAnchor.closed_trades || [])]
                      .sort((a, b) => (b.closed_at || '').localeCompare(a.closed_at || ''))
                      .map((t, i) => (
                        <TableRow key={`closed-${i}`}>
                          <TableCell>{t.underlying}</TableCell>
                          <TableCell>{fmtSpreadStrikes(t)}</TableCell>
                          <TableCell>{(t.opened_at || '').substring(0, 10)}</TableCell>
                          <TableCell>{(t.closed_at || '').substring(0, 10)}</TableCell>
                          <TableCell align="right">{(t.hold_days ?? 0).toFixed(1)}d</TableCell>
                          <TableCell align="right">{formatCurrency(t.entry_credit_total ?? 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(t.close_debit_total ?? 0)}</TableCell>
                          <TableCell align="right" sx={{ color: (t.realized_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }}>{formatCurrency(t.realized_pnl ?? 0)}</TableCell>
                          <TableCell><Chip size="small" variant="outlined" label={(t.exit_reason || t.exit_kind || '').replace(/_/g, ' ')} /></TableCell>
                          <TableCell align="right">{((t.pct_of_credit_captured ?? 0) * 100).toFixed(0)}%</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Underlying performance: realized (closed) + open unrealized per name */}
          {(() => {
            const byName: { [u: string]: { realized: number; closed: number; unrealized: number; open: number } } = {};
            (pcsAnchor.closed_trades || []).forEach(t => {
              const k = t.underlying;
              if (!byName[k]) byName[k] = { realized: 0, closed: 0, unrealized: 0, open: 0 };
              byName[k].realized += t.realized_pnl ?? 0;
              byName[k].closed += 1;
            });
            (pcsAnchor.open_spreads || []).forEach(s => {
              const k = s.underlying;
              if (!byName[k]) byName[k] = { realized: 0, closed: 0, unrealized: 0, open: 0 };
              byName[k].unrealized += s.unrealized_pnl ?? 0;
              byName[k].open += 1;
            });
            const rows = Object.entries(byName).sort((a, b) => (b[1].realized + b[1].unrealized) - (a[1].realized + a[1].unrealized));
            if (rows.length === 0) return null;
            return (
              <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Underlying Performance</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Underlying</TableCell>
                        <TableCell align="right">Open</TableCell>
                        <TableCell align="right">Closed</TableCell>
                        <TableCell align="right">Realized</TableCell>
                        <TableCell align="right">Open Unrealized</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(([name, v]) => (
                        <TableRow key={name}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{name}</TableCell>
                          <TableCell align="right">{v.open}</TableCell>
                          <TableCell align="right">{v.closed}</TableCell>
                          <TableCell align="right" sx={{ color: v.realized >= 0 ? '#2e7d32' : '#c62828' }}>{formatCurrency(v.realized)}</TableCell>
                          <TableCell align="right" sx={{ color: v.unrealized >= 0 ? '#2e7d32' : '#c62828' }}>{formatCurrency(v.unrealized)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}
        </>
      )}

      {/* ============ CSP WEEKLY-WHEEL VIEW ============ */}
      {!loading && !error && isWheel && wheelAnchor && (
        <>
          {wheelAnchor.period_source === 'unavailable' || wheelAnchor.period_source === 'empty' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Period analytics are not available yet for this instance (datastore {wheelAnchor.period_source}).
            </Alert>
          ) : null}

          {/* Row 1 — Income scoreboard (the hero). Premium collected = income engine,
              Realized (booked) = locked-in result. Today / This week / Since inception. */}
          <Typography variant="overline" sx={{ opacity: 0.7, display: 'block', mb: 0.5 }}>
            Income scoreboard — premium collected &amp; realized (booked)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {([
              { label: 'Today', prem: wPerfToday?.premium_collected ?? wheelAnchor.premium_collected_today_gross ?? 0, real: wPerfToday?.realized_pnl ?? wheelAnchor.realized_pnl_today ?? 0, sub: wheelAnchor.date_et },
              { label: 'This Week', prem: wPerfWtd?.premium_collected ?? wheelAnchor.period?.week_to_date.premium_collected ?? 0, real: wPerfWtd?.realized_pnl ?? wheelAnchor.period?.week_to_date.realized_pnl ?? 0, sub: `since ${wheelAnchor.period?.week_start || 'Mon'}` },
              { label: 'Since Inception', prem: wPerfCum?.premium_collected ?? wheelAnchor.period?.cumulative.premium_collected ?? 0, real: wPerfCum?.realized_pnl ?? wheelAnchor.period?.cumulative.realized_pnl ?? 0, sub: 'cumulative' },
            ]).map((g) => (
              <Grid item xs={12} sm={4} key={g.label}>
                <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.12)', border: '1px solid', borderColor: 'rgba(76, 175, 80, 0.3)', height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>{g.label}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Premium collected</Typography>
                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                          {formatCurrency(g.prem)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>Realized (booked)</Typography>
                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold', color: g.real > 0 ? '#4caf50' : g.real < 0 ? '#f44336' : 'text.secondary' }}>
                          {formatCurrency(g.real)}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.5 }}>{g.sub}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Row 2 — Position & risk (unrealized / mark-to-market). v3 relabel
              (2026-09-04): never say "paper" for unrealized P&L — since the
              GROK live cutover, "paper" means the retired paper ACCOUNT and
              reads as if archived history is blended into live numbers. */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={6} md={3}>
              <Tooltip arrow title={
                wPnl ? (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>Realized (booked): {formatCurrency(wPnl.realized_cumulative)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>Unrealized (open positions): {formatCurrency(wPnl.unrealized_total)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', pl: 1, opacity: 0.8 }}>· shares {formatCurrency(wPnl.unrealized_shares)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', pl: 1, opacity: 0.8 }}>· options {formatCurrency(wPnl.unrealized_options)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>Gross premium collected: {formatCurrency(wPnl.premium_collected_cumulative)}</Typography>
                  </Box>
                ) : 'realized + unrealized'
              }>
                <Card sx={{
                  bgcolor: (wPnl?.total_pnl ?? wPerfCum?.total_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.18)' : 'rgba(244, 67, 54, 0.18)',
                  border: '1px solid', borderColor: (wPnl?.total_pnl ?? 0) >= 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)', height: '100%', cursor: 'help',
                }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Total P&L <span style={{ opacity: 0.6, fontWeight: 400 }}>(incl. unrealized)</span></Typography>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: (wPnl?.total_pnl ?? wPerfCum?.total_pnl ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                      {formatCurrency(wPnl?.total_pnl ?? wPerfCum?.total_pnl ?? 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      realized {formatCurrency(wPnl?.realized_cumulative ?? wPerfCum?.realized_pnl ?? 0)} + unrealized {formatCurrency(wPnl?.unrealized_total ?? wPerfCum?.unrealized_pnl ?? 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.7 }}>Mark-to-market today <span style={{ opacity: 0.6, fontWeight: 400 }}>(incl. unrealized)</span></Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: (wPerfToday?.day_pnl_mtm ?? wheelAnchor.day_pnl_estimate ?? 0) >= 0 ? '#81c784' : '#e57373' }}>
                    {formatCurrency(wPerfToday?.day_pnl_mtm ?? wheelAnchor.day_pnl_estimate ?? 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    today's mark move on the open book
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.12)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Capital Deployed</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(wCap?.capital_deployed ?? wheelAnchor.cash_locked_total ?? 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                    {wCap ? `${(wCap.pct_of_equity * 100).toFixed(1)}% of equity` : (wheelAnchor.account.equity > 0 ? `${(((wheelAnchor.cash_locked_total ?? 0) / wheelAnchor.account.equity) * 100).toFixed(0)}% of equity` : '—')}
                  </Typography>
                  {wCap && (
                    <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                      Cash available {formatCurrency(wCap.cash_available)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ bgcolor: 'rgba(249, 168, 37, 0.14)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Shares Held</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {(wCap?.shares_held_total ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                    across {wCap?.names_holding_shares ?? 0} name{(wCap?.names_holding_shares ?? 0) !== 1 ? 's' : ''} · all covered
                  </Typography>
                  {wCap && (
                    <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                      mkt {formatCurrency(wCap.shares_market_value)} vs cost {formatCurrency(wCap.shares_cost_basis)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Row 3 — Win / quality stats */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4}>
              <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Win Rate (Cumulative)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: (wPerfCum?.win_rate ?? wheelAnchor.period?.cumulative.win_rate ?? 0) >= 0.5 ? '#4caf50' : '#f44336' }}>
                    {fmtWinRate(wPerfCum?.win_rate ?? wheelAnchor.period?.cumulative.win_rate)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {wPerfCum?.wins ?? wheelAnchor.period?.cumulative.wins ?? 0}W / {wPerfCum?.losses ?? wheelAnchor.period?.cumulative.losses ?? 0}L · WTD {fmtWinRate(wPerfWtd?.win_rate ?? wheelAnchor.period?.week_to_date.win_rate)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Winning days / Losing days</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>{wheelAnchor.winning_days ?? 0}W</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main' }}>{wheelAnchor.losing_days ?? 0}L</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>active trading days</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ opacity: 0.8 }}>Assignments</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f9a825' }}>
                    {wPerfCum?.num_assignments ?? wheelAnchor.period?.cumulative.num_assignments ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    cumulative · WTD {wPerfWtd?.num_assignments ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Wheel Status */}
          {wheelAnchor.wheel_state && Object.keys(wheelAnchor.wheel_state).length > 0 && (
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <Typography variant="h6">Wheel Status</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {['NO_POSITION', 'SHORT_PUT', 'LONG_SHARES', 'SHORT_CALL'].map((st) => (
                    <Chip
                      key={st}
                      size="small"
                      label={`${st.replace('_', ' ')}: ${wheelAnchor.wheel_state_counts?.[st] ?? 0}`}
                      sx={{ bgcolor: wheelStateColor(st), color: '#fff', fontWeight: 'bold', fontSize: '0.68rem' }}
                    />
                  ))}
                </Box>
              </Box>
              <Grid container spacing={1}>
                {Object.entries(wheelAnchor.wheel_state).map(([sym, ws]) => (
                  <Grid item xs={6} sm={4} md={3} lg={12 / 7} key={sym}>
                    <Paper variant="outlined" sx={{ p: 1, borderLeft: `4px solid ${wheelStateColor(ws.state)}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight="bold">{sym}</Typography>
                        <Typography variant="caption" sx={{ color: wheelStateColor(ws.state), fontWeight: 'bold' }}>
                          {ws.state.replace('_', ' ')}
                        </Typography>
                      </Box>
                      {ws.state === 'SHORT_PUT' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ${ws.strike} put · {ws.dte}d · cr {formatCurrency(ws.entry_credit_total || 0)}
                        </Typography>
                      )}
                      {ws.state === 'SHORT_CALL' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ${ws.strike} call · {ws.dte}d
                        </Typography>
                      )}
                      {ws.state === 'LONG_SHARES' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {ws.shares_held} sh @ {ws.cost_basis_per_share != null ? formatCurrency(ws.cost_basis_per_share) : '—'}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        locked {formatCurrency(ws.cash_locked || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          {/* Assignment Risk */}
          {(wheelAnchor.assignment_risk || []).length > 0 && (
            <Alert severity="warning" icon={false} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                ⚠️ Assignment Risk — ITM puts expiring soon ({wheelAnchor.assignment_risk!.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Symbol</strong></TableCell>
                      <TableCell align="right"><strong>Spot</strong></TableCell>
                      <TableCell align="right"><strong>Strike</strong></TableCell>
                      <TableCell align="right"><strong>Distance</strong></TableCell>
                      <TableCell align="right"><strong>DTE</strong></TableCell>
                      {!isMobile && <TableCell align="right"><strong>Cost Basis if Assigned</strong></TableCell>}
                      <TableCell align="right"><strong>Cash Required</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wheelAnchor.assignment_risk!.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{r.symbol}</TableCell>
                        <TableCell align="right">{formatCurrency(r.spot)}</TableCell>
                        <TableCell align="right">{formatCurrency(r.strike)}</TableCell>
                        <TableCell align="right" sx={{ color: r.distance_pct < 0 ? '#f44336' : '#4caf50', fontWeight: 'bold' }}>
                          {r.distance_pct >= 0 ? '+' : ''}{r.distance_pct.toFixed(2)}%
                        </TableCell>
                        <TableCell align="right">{r.dte}</TableCell>
                        {!isMobile && <TableCell align="right">{formatCurrency(r.cost_basis_if_assigned)}</TableCell>}
                        <TableCell align="right">{formatCurrency(r.cash_required_if_assigned)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Alert>
          )}

          {/* Assigned Shares / Covered Calls */}
          {(wheelAnchor.assigned_shares || []).length > 0 && (
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Assigned Shares & Covered Calls</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Symbol</strong></TableCell>
                      <TableCell align="right"><strong>Shares</strong></TableCell>
                      <TableCell align="right"><strong>Cost Basis</strong></TableCell>
                      <TableCell align="right"><strong>Current</strong></TableCell>
                      <TableCell align="right"><strong>Unrealized</strong></TableCell>
                      <TableCell><strong>Covered Call</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wheelAnchor.assigned_shares!.map((s, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{s.symbol}</TableCell>
                        <TableCell align="right">{s.shares}</TableCell>
                        <TableCell align="right">{formatCurrency(s.cost_basis_per_share)}</TableCell>
                        <TableCell align="right">{formatCurrency(s.current_price)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: s.unrealized_pnl_shares >= 0 ? '#4caf50' : '#f44336' }}>
                          {formatCurrency(s.unrealized_pnl_shares)}
                        </TableCell>
                        <TableCell>
                          {s.covered_call_active
                            ? <Chip size="small" label={`$${s.call_strike} · ${s.call_dte}d`} sx={{ bgcolor: wheelStateColor('SHORT_CALL'), color: '#fff', fontWeight: 'bold' }} />
                            : <Typography variant="caption" color="text.secondary">none</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Weekly performance chart (NEW) — premium + realized bars, cumulative line */}
          {(wheelAnchor.weekly_series || []).length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                Weekly Performance — Premium &amp; Realized
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : isTablet ? 300 : 380}>
                <ComposedChart data={wheelAnchor.weekly_series!.map(w => ({
                  week: w.week_start.substring(5),
                  premium: parseFloat((w.premium_collected || 0).toFixed(2)),
                  realized: parseFloat((w.realized_pnl || 0).toFixed(2)),
                  cumulative: parseFloat((w.cumulative_realized || 0).toFixed(2)),
                }))} margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 10 : 12 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 10 : 12 }} tickFormatter={(v) => `$${v}`} />
                  <RechartsTooltip formatter={(value: any) => formatCurrency(value)} contentStyle={{ fontSize: isMobile ? 11 : 14 }} labelFormatter={(l) => `Week of ${l}`} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }} />
                  <Bar yAxisId="left" dataKey="premium" name="Premium Collected" fill="#4caf50" radius={[6, 6, 0, 0]} />
                  <Bar yAxisId="left" dataKey="realized" name="Realized P&L" radius={[6, 6, 0, 0]}>
                    {wheelAnchor.weekly_series!.map((w, index) => (
                      <Cell key={`ww-${index}`} fill={(w.realized_pnl || 0) >= 0 ? '#1976d2' : '#d32f2f'} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative Realized" stroke="#f9a825" strokeWidth={2} dot={{ r: isMobile ? 3 : 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Daily realized P&L & cumulative chart (from daily_series) */}
          {(wheelAnchor.daily_series || []).length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
                Daily Realized P&L &amp; Cumulative Performance
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 250 : isTablet ? 300 : 400}>
                <ComposedChart data={wheelAnchor.daily_series!.map(p => ({ date: p.date.substring(5), premium: parseFloat((p.premium_collected || 0).toFixed(2)), daily: parseFloat(p.daily_realized.toFixed(2)), cumulative: parseFloat(p.cumulative_realized.toFixed(2)) }))} margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 10 : 12 }} tickFormatter={(v) => `$${v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 10 : 12 }} tickFormatter={(v) => `$${v}`} />
                  <RechartsTooltip formatter={(value: any) => formatCurrency(value)} contentStyle={{ fontSize: isMobile ? 11 : 14 }} />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }} />
                  <Bar yAxisId="left" dataKey="premium" name="Premium Collected" fill="#4caf50" fillOpacity={0.35} radius={[6, 6, 0, 0]} />
                  <Bar yAxisId="left" dataKey="daily" name="Daily Realized" radius={[8, 8, 0, 0]}>
                    {wheelAnchor.daily_series!.map((entry, index) => (
                      <Cell key={`wc-${index}`} fill={entry.daily_realized >= 0 ? '#2e7d32' : '#d32f2f'} />
                    ))}
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative Realized" stroke="#1976d2" strokeWidth={2} dot={{ r: isMobile ? 3 : 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Symbol Performance (per_symbol_period) */}
          {wheelAnchor.per_symbol_period && Object.keys(wheelAnchor.per_symbol_period).length > 0 && (
            <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Symbol Performance</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Symbol</strong></TableCell>
                      <TableCell align="right"><strong>Today Realized</strong></TableCell>
                      <TableCell align="right"><strong>Period Realized</strong></TableCell>
                      <TableCell align="right"><strong>Premium Collected</strong></TableCell>
                      {!isMobile && <TableCell align="right"><strong>Entries / Exits</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(wheelAnchor.per_symbol_period)
                      .sort((a, b) => b[1].period_realized - a[1].period_realized)
                      .map(([sym, d]) => (
                        <TableRow key={sym} hover>
                          <TableCell>
                            <Chip label={sym} size="small" color="primary" onClick={() => handleSymbolClick(sym)} sx={{ fontWeight: 'bold', cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: d.today_realized >= 0 ? '#4caf50' : '#f44336' }}>
                            {d.today_realized !== 0 ? formatCurrency(d.today_realized) : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: d.period_realized >= 0 ? '#4caf50' : '#f44336' }}>
                            {formatCurrency(d.period_realized)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#4caf50' }}>{formatCurrency(d.period_premium_collected)}</TableCell>
                          {!isMobile && <TableCell align="right" sx={{ color: 'text.secondary' }}>{d.period_entries} / {d.period_exits}</TableCell>}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Period Summary & Charts (legacy intraday / pairs format) */}
      {!loading && !error && periodMetrics && !isWheel && !isPcs && !isCspWheelV2 && (
        <>
          {/* Period Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Total P&L */}
            <Grid item xs={12} sm={6} md={dailyDataMap.size === 1 ? 6 : 3}>
              <Card sx={{ 
                bgcolor: periodMetrics.totalPnL >= 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                border: '1px solid',
                borderColor: periodMetrics.totalPnL >= 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)',
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssessmentIcon sx={{ mr: 1, color: 'text.primary', opacity: 0.7 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary', opacity: 0.8 }}>
                      {dailyDataMap.size === 1 ? 'Daily P&L' : 'Total P&L'}
                    </Typography>
                  </Box>
                  <Typography 
                    variant={isMobile ? 'h5' : 'h4'}
                    sx={{ 
                      fontWeight: 'bold',
                      color: periodMetrics.totalPnL >= 0 ? '#2e7d32' : '#c62828'
                    }}
                  >
                    {formatCurrency(periodMetrics.totalPnL)}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: periodMetrics.totalPnL >= 0 ? '#2e7d32' : '#c62828',
                      fontWeight: 'bold',
                      mb: 0.5
                    }}
                  >
                    ({periodMetrics.totalPnL >= 0 ? '+' : ''}{periodMetrics.totalPnLPercent.toFixed(2)}%)
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                    {dailyDataMap.size === 1 ? startDate : `Period: ${dailyDataMap.size} days`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Average Daily P&L - Only show for multiple days */}
            {dailyDataMap.size > 1 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ShowChartIcon sx={{ mr: 1, color: 'text.primary', opacity: 0.7 }} />
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        Avg Daily P&L
                      </Typography>
                    </Box>
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'}
                      sx={{ 
                        fontWeight: 'bold',
                        color: periodMetrics.avgDailyPnL >= 0 ? '#4caf50' : '#f44336'
                      }}
                    >
                      {formatCurrency(periodMetrics.avgDailyPnL)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                      Win Rate: {periodMetrics.winRate.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Best Day - Only show for multiple days */}
            {dailyDataMap.size > 1 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.2)',
                  border: '1px solid',
                  borderColor: 'rgba(76, 175, 80, 0.4)',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmojiEventsIcon sx={{ mr: 1, color: 'text.primary', opacity: 0.7 }} />
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary', opacity: 0.8 }}>
                        Best Day
                      </Typography>
                    </Box>
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'}
                      sx={{ fontWeight: 'bold', color: '#2e7d32' }}
                    >
                      {periodMetrics.bestDay ? formatCurrency(periodMetrics.bestDay.pnl) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                      {periodMetrics.bestDay?.date || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Worst Day - Only show for multiple days */}
            {dailyDataMap.size > 1 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  bgcolor: periodMetrics.worstDay && periodMetrics.worstDay.pnl < 0 
                    ? 'rgba(244, 67, 54, 0.2)'  // Red for negative
                    : 'rgba(46, 125, 50, 0.2)',  // Green for positive
                  border: '1px solid',
                  borderColor: periodMetrics.worstDay && periodMetrics.worstDay.pnl < 0
                    ? 'rgba(244, 67, 54, 0.4)'
                    : 'rgba(46, 125, 50, 0.4)',
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TrendingDownIcon sx={{ mr: 1, color: 'text.primary', opacity: 0.7 }} />
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary', opacity: 0.8 }}>
                        Worst Day
                      </Typography>
                    </Box>
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'}
                      sx={{ 
                        fontWeight: 'bold', 
                        color: periodMetrics.worstDay && periodMetrics.worstDay.pnl < 0 
                          ? '#c62828'  // Red for negative
                          : '#2e7d32'  // Green for positive
                      }}
                    >
                      {periodMetrics.worstDay ? formatCurrency(periodMetrics.worstDay.pnl) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                      {periodMetrics.worstDay?.date || '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Win Rate Card - Only show for single day */}
            {dailyDataMap.size === 1 && (
              <Grid item xs={12} sm={6} md={6}>
                <Card sx={{ bgcolor: 'rgba(66, 66, 66, 0.4)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ShowChartIcon sx={{ mr: 1, color: 'text.primary', opacity: 0.7 }} />
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                        Trade Performance
                      </Typography>
                    </Box>
                    <Typography 
                      variant={isMobile ? 'h5' : 'h4'}
                      sx={{ 
                        fontWeight: 'bold',
                        color: periodMetrics.winRate >= 50 ? '#4caf50' : '#f44336'
                      }}
                    >
                      {periodMetrics.winRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.6 }}>
                      Win Rate: {periodMetrics.winningDays}W / {periodMetrics.losingDays}L
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Win/Loss Stats */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Performance Stats</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Winning Days</Typography>
                      <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        {periodMetrics.winningDays}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Losing Days</Typography>
                      <Typography variant="h5" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        {periodMetrics.losingDays}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary">Realized P&L (Closed Pairs)</Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: periodMetrics.totalRealizedPnL >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {formatCurrency(periodMetrics.totalRealizedPnL)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Symbol Performance - Today vs All Time */}
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Symbol Performance</Typography>
                  
                  {(() => {
                    // Get today's data (most recent day in the range)
                    const sortedDates = Array.from(dailyDataMap.keys()).sort();
                    const todayDate = sortedDates[sortedDates.length - 1];
                    const todayData = dailyDataMap.get(todayDate);
                    const todaySymbols = todayData?.eodData.per_symbol_totals || {};
                    
                    // Get all symbols from both today and period
                    const allSymbols = new Set([
                      ...Object.keys(todaySymbols),
                      ...Object.keys(periodMetrics.symbolPerformance)
                    ]);
                    
                    // Create combined data
                    const combinedData = Array.from(allSymbols).map(symbol => ({
                      symbol,
                      todayPnl: todaySymbols[symbol]?.realized_pnl || 0,
                      todayTrades: todaySymbols[symbol]?.trades || 0,
                      periodPnl: periodMetrics.symbolPerformance[symbol]?.pnl || 0,
                      periodTrades: periodMetrics.symbolPerformance[symbol]?.trades || 0,
                      tradedToday: (todaySymbols[symbol]?.trades || 0) > 0,
                    }));
                    
                    // Sort: Symbols traded today first (by today's P&L), then by period P&L
                    combinedData.sort((a, b) => {
                      // Prioritize symbols traded today
                      if (a.tradedToday && !b.tradedToday) return -1;
                      if (!a.tradedToday && b.tradedToday) return 1;
                      
                      // If both traded today, sort by today's P&L (descending, best to worst including losses)
                      if (a.tradedToday && b.tradedToday) {
                        return b.todayPnl - a.todayPnl;
                      }
                      
                      // Otherwise sort by period P&L
                      return b.periodPnl - a.periodPnl;
                    });
                    
                    return (
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Symbol</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Today</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 0.5 }}>Period</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {combinedData.slice(0, 10).map((row) => (
                                <TableRow key={row.symbol} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                  <TableCell sx={{ py: 0.5 }}>
                                    <Chip 
                                      label={row.symbol} 
                                      size="small" 
                                      color="primary" 
                                      onClick={() => handleSymbolClick(row.symbol)}
                                      sx={{ 
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '0.7rem',
                                        height: 20,
                                        '&:hover': {
                                          opacity: 0.8,
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.5 }}>
                                    <Box>
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          color: row.todayPnl >= 0 ? '#4caf50' : '#f44336',
                                          display: 'block',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {row.todayPnl !== 0 ? formatCurrency(row.todayPnl) : '-'}
                                      </Typography>
                                      {row.todayTrades > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                          {row.todayTrades} trade{row.todayTrades !== 1 ? 's' : ''}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 0.5 }}>
                                    <Box>
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          color: row.periodPnl >= 0 ? '#4caf50' : '#f44336',
                                          display: 'block',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {formatCurrency(row.periodPnl)}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {row.periodTrades} trade{row.periodTrades !== 1 ? 's' : ''}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ px: 1 }}>
              Daily P&L & Cumulative Performance
            </Typography>
            <ResponsiveContainer width="100%" height={isMobile ? 250 : isTablet ? 300 : 400}>
              <ComposedChart data={chartDataWithCumulative} margin={{ top: 20, right: 20, bottom: 20, left: isMobile ? 0 : 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={isMobile ? -45 : 0}
                  textAnchor={isMobile ? "end" : "middle"}
                  height={isMobile ? 60 : 30}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right"
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <RechartsTooltip 
                  formatter={(value: any) => formatCurrency(value)}
                  contentStyle={{ fontSize: isMobile ? 11 : 14 }}
                />
                <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 14 }} />
                <Bar yAxisId="left" dataKey="dailyPnL" name="Daily P&L" radius={[8, 8, 0, 0]}>
                  {chartDataWithCumulative.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.dailyPnL >= 0 ? '#2e7d32' : '#d32f2f'} />
                  ))}
                  <LabelList 
                    dataKey="percentage" 
                    position="top" 
                    formatter={(value: number) => `${value >= 0 ? '+' : ''}${value}%`}
                    style={{ fontSize: isMobile ? 9 : 11, fill: '#ffffff', fontWeight: 'bold' }}
                  />
                </Bar>
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="cumulativePnL" 
                  name="Cumulative P&L" 
                  stroke="#1976d2" 
                  strokeWidth={2}
                  dot={{ r: isMobile ? 3 : 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {/* Daily Breakdown - shared by legacy and GROK-wheel formats (PCS & CSP-wheel v2 have their own panels) */}
      {!loading && !error && dailyDataMap.size > 0 && !isPcs && !isCspWheelV2 && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
            Daily Breakdown
          </Typography>
          {Array.from(dailyDataMap.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([date, day]) => {
              const isExpanded = expandedDays.has(date);
              const dailyPnL = day.actual_daily_pnl ?? day.eodData.realized_pnl_day_from_pairs;
              const winners = day.eodData.pairs.filter(p => p.realized_pnl > 0).length;
              const losers = day.eodData.pairs.filter(p => p.realized_pnl < 0).length;
              // New CSP / premium-selling format: positions held overnight
              const isCsp = !!(day.eodData.today_trades || day.eodData.open_positions || day.eodData.day_pnl_estimate !== undefined);
              const isWheelDay = day.eodData.realized_pnl_today !== undefined || day.eodData.period !== undefined;
              const todayTrades = day.eodData.today_trades || [];
              const openPositions = day.eodData.open_positions || [];
              const exitsToday = day.eodData.exits_today || [];

              return (
                <Accordion 
                  key={date} 
                  expanded={isExpanded}
                  onChange={() => toggleDayExpansion(date)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {date}
                        </Typography>
                        {isCsp ? (
                          <>
                            <Chip
                              label={`${todayTrades.length} trade${todayTrades.length !== 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`${openPositions.length} open`}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          </>
                        ) : (
                          <Chip 
                            label={`${day.eodData.num_pairs} pairs`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography 
                            variant={isMobile ? "body1" : "h6"}
                            sx={{ 
                              fontWeight: 'bold',
                              color: dailyPnL >= 0 ? 'success.main' : 'error.main' 
                            }}
                          >
                            {formatCurrency(dailyPnL)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {isWheelDay
                              ? `${formatCurrency(day.eodData.realized_pnl_today || 0)} realized`
                              : isCsp
                                ? `${formatCurrency(day.eodData.net_cashflow_today || 0)} premium`
                                : `${winners}W / ${losers}L`}
                          </Typography>
                        </Box>
                        {dailyPnL >= 0 ? (
                          <TrendingUpIcon color="success" />
                        ) : (
                          <TrendingDownIcon color="error" />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Account Summary */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Portfolio Value</Typography>
                          <Typography variant="h6">{formatCurrency(day.eodData.account.equity)}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Cash</Typography>
                          <Typography variant="h6">{formatCurrency(day.eodData.account.cash)}</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Buying Power</Typography>
                          <Typography variant="h6">{formatCurrency(day.eodData.account.buying_power)}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* CSP / premium-selling: premium summary, today's trades, overnight positions */}
                    {isCsp && (
                      <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.12)' }}>
                              <Typography variant="caption" color="text.secondary">Premium Collected</Typography>
                              <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                                {formatCurrency(day.eodData.premium_collected_today_gross || 0)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">Premium Paid</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(day.eodData.premium_paid_today_gross || 0)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                              <Typography variant="caption" color="text.secondary">Net Cashflow</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(day.eodData.net_cashflow_today || 0)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: dailyPnL >= 0 ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)' }}>
                              <Typography variant="caption" color="text.secondary">Day P&L (MTM)</Typography>
                              <Typography variant="h6" sx={{ color: dailyPnL >= 0 ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                                {formatCurrency(dailyPnL)}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Today's Trades */}
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Today's Trades ({todayTrades.length})
                        </Typography>
                        {todayTrades.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            No trades executed on this day
                          </Typography>
                        ) : (
                          <TableContainer sx={{ maxHeight: 400, mb: 2 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  {!isMobile && <TableCell><strong>Time</strong></TableCell>}
                                  <TableCell><strong>Action</strong></TableCell>
                                  <TableCell><strong>Underlying</strong></TableCell>
                                  <TableCell><strong>Type</strong></TableCell>
                                  <TableCell align="right"><strong>Strike</strong></TableCell>
                                  {!isMobile && <TableCell><strong>Expiry</strong></TableCell>}
                                  <TableCell align="right"><strong>Qty</strong></TableCell>
                                  <TableCell align="right"><strong>Fill</strong></TableCell>
                                  <TableCell align="right"><strong>Premium</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {todayTrades.map((t, index) => (
                                  <TableRow key={index} hover>
                                    {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{formatDateTime(t.time_et)}</TableCell>}
                                    <TableCell>
                                      <Chip label={t.action} size="small" color={t.action === 'SELL' ? 'success' : 'warning'} sx={{ fontWeight: 'bold' }} />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{t.underlying}</TableCell>
                                    <TableCell>
                                      <Chip label={t.option_type} size="small" variant="outlined" color={t.option_type === 'PUT' ? 'info' : 'secondary'} />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(t.strike)}</TableCell>
                                    {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{t.expiry}</TableCell>}
                                    <TableCell align="right">{t.qty}</TableCell>
                                    <TableCell align="right">{formatCurrency(t.fill_price)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: t.cash_flow >= 0 ? '#4caf50' : '#f44336' }}>
                                      {formatCurrency(t.premium)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}

                        {/* Exits / Closes Today (realized) */}
                        {exitsToday.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                              Closes Today — Realized ({exitsToday.length})
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400, mb: 2 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    {!isMobile && <TableCell><strong>Time</strong></TableCell>}
                                    <TableCell><strong>Event</strong></TableCell>
                                    <TableCell><strong>Underlying</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell align="right"><strong>Strike</strong></TableCell>
                                    {!isMobile && <TableCell align="right"><strong>Fill</strong></TableCell>}
                                    <TableCell align="right"><strong>Realized P&L</strong></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {exitsToday.map((x, index) => (
                                    <TableRow key={index} sx={{ bgcolor: x.realized_pnl >= 0 ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)' }}>
                                      {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{formatDateTime(x.time_et)}</TableCell>}
                                      <TableCell>
                                        <Chip
                                          label={x.event.replace(/_/g, ' ')}
                                          size="small"
                                          color={x.event.includes('ASSIGNED') || x.event.includes('CALLED_AWAY') ? 'warning' : 'default'}
                                          variant="outlined"
                                          sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>{x.underlying}</TableCell>
                                      <TableCell>
                                        <Chip label={x.option_type} size="small" variant="outlined" color={x.option_type === 'PUT' ? 'info' : 'secondary'} />
                                      </TableCell>
                                      <TableCell align="right">{formatCurrency(x.strike)}</TableCell>
                                      {!isMobile && <TableCell align="right">{x.fill_price != null ? formatCurrency(x.fill_price) : '-'}</TableCell>}
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: x.realized_pnl >= 0 ? '#4caf50' : '#f44336' }}>
                                        {formatCurrency(x.realized_pnl)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        )}

                        {/* Open Positions held overnight */}
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Open Positions — Held Overnight ({openPositions.length})
                          {day.eodData.open_positions_summary && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              · Unrealized {formatCurrency(day.eodData.open_positions_summary.total_unrealized_pnl)}
                            </Typography>
                          )}
                        </Typography>
                        {openPositions.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                            No open positions
                          </Typography>
                        ) : (
                          <TableContainer sx={{ maxHeight: 400, mb: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Underlying</strong></TableCell>
                                  <TableCell><strong>Type</strong></TableCell>
                                  <TableCell align="right"><strong>Strike</strong></TableCell>
                                  {!isMobile && <TableCell><strong>Expiry</strong></TableCell>}
                                  {!isMobile && <TableCell align="right"><strong>DTE</strong></TableCell>}
                                  <TableCell><strong>Side</strong></TableCell>
                                  <TableCell align="right"><strong>Qty</strong></TableCell>
                                  {!isMobile && <TableCell align="right"><strong>Entry</strong></TableCell>}
                                  <TableCell align="right"><strong>Mark</strong></TableCell>
                                  <TableCell align="right"><strong>Premium</strong></TableCell>
                                  <TableCell align="right"><strong>Unreal. P&L</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {openPositions.map((p, index) => (
                                  <TableRow key={index} sx={{ bgcolor: p.unrealized_pnl >= 0 ? 'rgba(76,175,80,0.12)' : 'rgba(244,67,54,0.12)' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{p.underlying}</TableCell>
                                    <TableCell>
                                      <Chip label={p.option_type} size="small" variant="outlined" color={p.option_type === 'PUT' ? 'info' : 'secondary'} />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(p.strike)}</TableCell>
                                    {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{p.expiry}</TableCell>}
                                    {!isMobile && <TableCell align="right">{p.dte}</TableCell>}
                                    <TableCell>
                                      <Chip label={p.side.toUpperCase()} size="small" color={p.side === 'short' ? 'warning' : 'success'} sx={{ fontWeight: 'bold' }} />
                                    </TableCell>
                                    <TableCell align="right">{p.qty}</TableCell>
                                    {!isMobile && <TableCell align="right">{formatCurrency(p.avg_entry_price)}</TableCell>}
                                    <TableCell align="right">{formatCurrency(p.current_price)}</TableCell>
                                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatCurrency(p.premium_collected)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: p.unrealized_pnl >= 0 ? '#4caf50' : '#f44336' }}>
                                      {formatCurrency(p.unrealized_pnl)}
                                      <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        {(p.unrealized_pnl_pct * 100).toFixed(1)}%
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </>
                    )}

                    {/* Instrument Type Breakdown for HYBRID */}
                    {instanceName === 'HYBRID' && day.eodData.per_instrument_type && day.eodData.per_instrument_type.EQUITY && day.eodData.per_instrument_type.OPTION && (
                      <>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          📊 Instrument Type Breakdown
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} sm={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.5,
                                bgcolor: 'rgba(33, 150, 243, 0.1)',
                                border: '1px solid rgba(33, 150, 243, 0.4)',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip label="EQUITY" color="primary" size="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {day.eodData.per_instrument_type.EQUITY.unique_symbols || 0} symbols
                                </Typography>
                              </Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: (day.eodData.per_instrument_type.EQUITY.realized_pnl || 0) >= 0 ? '#4caf50' : '#f44336',
                                  fontWeight: 'bold'
                                }}
                              >
                                {formatCurrency(day.eodData.per_instrument_type.EQUITY.realized_pnl || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {day.eodData.per_instrument_type.EQUITY.trades || 0} trade{(day.eodData.per_instrument_type.EQUITY.trades || 0) !== 1 ? 's' : ''}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1.5,
                                bgcolor: 'rgba(3, 169, 244, 0.1)',
                                border: '1px solid rgba(3, 169, 244, 0.4)',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip label="OPTION" color="info" size="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {day.eodData.per_instrument_type.OPTION.unique_symbols || 0} symbols
                                </Typography>
                              </Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: (day.eodData.per_instrument_type.OPTION.realized_pnl || 0) >= 0 ? '#4caf50' : '#f44336',
                                  fontWeight: 'bold'
                                }}
                              >
                                {formatCurrency(day.eodData.per_instrument_type.OPTION.realized_pnl || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {day.eodData.per_instrument_type.OPTION.trades || 0} trade{(day.eodData.per_instrument_type.OPTION.trades || 0) !== 1 ? 's' : ''}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </>
                    )}

                    {/* Per Symbol Performance - This Day vs Period (legacy pairs format only) */}
                    {!isCsp && (
                    <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                      Symbol Performance
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      {Object.entries(day.eodData.per_symbol_totals).map(([symbol, data]) => {
                        // Get period performance for this symbol
                        const periodData = periodMetrics?.symbolPerformance[symbol];
                        const periodPnl = periodData?.pnl || 0;
                        const periodTrades = periodData?.trades || 0;
                        
                        return (
                          <Grid item xs={6} sm={4} md={3} key={symbol}>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1,
                                bgcolor: data.realized_pnl >= 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                                border: `1px solid ${data.realized_pnl >= 0 ? 'rgba(76, 175, 80, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: 2,
                                }
                              }}
                              onClick={() => handleSymbolClick(symbol)}
                            >
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary' }}>
                                {symbol}
                              </Typography>
                              
                              {/* This Day Performance */}
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  This Day
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: data.realized_pnl >= 0 ? '#4caf50' : '#f44336',
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {formatCurrency(data.realized_pnl)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  {data.trades} trade{data.trades !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                              
                              {/* Period Performance */}
                              <Divider sx={{ my: 0.5 }} />
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  Period
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: periodPnl >= 0 ? '#4caf50' : '#f44336',
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {formatCurrency(periodPnl)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                                  {periodTrades} trade{periodTrades !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Trade Pairs Table */}
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Trade Pairs
                    </Typography>
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Symbol</strong></TableCell>
                            <TableCell><strong>Side</strong></TableCell>
                            {instanceName === 'HYBRID' && <TableCell><strong>Type</strong></TableCell>}
                            <TableCell align="right"><strong>Qty</strong></TableCell>
                            <TableCell align="right"><strong>Entry</strong></TableCell>
                            {!isMobile && <TableCell><strong>Entry Time</strong></TableCell>}
                            <TableCell align="right"><strong>Exit</strong></TableCell>
                            {!isMobile && <TableCell><strong>Exit Time</strong></TableCell>}
                            {!isMobile && <TableCell align="right"><strong>Hold (min)</strong></TableCell>}
                            <TableCell align="right"><strong>P&L</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {day.eodData.pairs.map((pair, index) => (
                            <TableRow 
                              key={index}
                              sx={{ 
                                bgcolor: pair.realized_pnl >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)',
                                '&:hover': { 
                                  bgcolor: pair.realized_pnl >= 0 ? 'rgba(76, 175, 80, 0.25)' : 'rgba(244, 67, 54, 0.25)',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  onClick={() => handleSymbolClick(pair.symbol)}
                                  sx={{ 
                                    color: '#90caf9', 
                                    textDecoration: 'none',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  {pair.symbol}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={getPositionSideLabel(pair.symbol, pair.side)} 
                                  size="small" 
                                  color={pair.side === 'LONG' ? 'success' : 'warning'}
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              {instanceName === 'HYBRID' && (
                                <TableCell>
                                  <Chip 
                                    label={pair.instrument_type || 'EQUITY'} 
                                    size="small" 
                                    color={pair.instrument_type === 'OPTION' ? 'info' : 'primary'}
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </TableCell>
                              )}
                              <TableCell align="right" sx={{ color: 'text.primary' }}>{pair.qty}</TableCell>
                              <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(pair.entry_price)}</TableCell>
                              {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{formatDateTime(pair.entry_time_et)}</TableCell>}
                              <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(pair.exit_price)}</TableCell>
                              {!isMobile && <TableCell sx={{ color: 'text.secondary' }}>{formatDateTime(pair.exit_time_et)}</TableCell>}
                              {!isMobile && <TableCell align="right" sx={{ color: 'text.primary' }}>{pair.holding_minutes.toFixed(1)}</TableCell>}
                              <TableCell 
                                align="right"
                                sx={{ 
                                  fontWeight: 'bold',
                                  color: pair.realized_pnl >= 0 ? '#4caf50' : '#f44336',
                                }}
                              >
                                {formatCurrency(pair.realized_pnl)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    </>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </>
      )}

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChartModal}
        symbol={selectedSymbol}
      />
    </Box>
  );
};

export default EODView;
